<template>
  <div :class="styles['auth-ai-preview']">
    <VSimpleLogoDark :class="styles['auth-ai-preview__logo']" />

    <h1 :class="styles['auth-ai-preview__headline']">
      {{ AUTH_AI_PREVIEW_HEADLINE }}
    </h1>

    <div
      :class="styles['auth-ai-preview__stage']"
      aria-hidden="true"
    >
      <span :class="styles['auth-ai-preview__connector']" />

      <p :class="styles['auth-ai-preview__pill']">
        <VaiSparkleIcon :class="styles['auth-ai-preview__pill-icon']" />
        {{ AUTH_AI_PREVIEW_PILL_LABEL }}
      </p>

      <div :class="styles['auth-ai-preview__viewport']">
        <ul
          :class="styles['auth-ai-preview__list']"
          :style="listStyle"
        >
          <span :class="styles['auth-ai-preview__list-connector']" />
          <li
            v-for="(step, index) in AUTH_AI_PREVIEW_STEPS"
            :key="step"
            :data-step-state="stepState(index)"
            :class="[
              styles['auth-ai-preview__step'],
              styles[`auth-ai-preview__step--${stepState(index)}`]
            ]"
          >
            <span :class="styles['auth-ai-preview__step-label']">{{ step }}</span>
            <VaiStepCheckIcon
              v-if="stepState(index) === AUTH_AI_PREVIEW_STEP_STATES.done"
              :class="styles['auth-ai-preview__step-check']"
            />
            <VaiStepSpinnerIcon
              v-else
              :class="styles['auth-ai-preview__step-spinner']"
            />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, useCssModule } from 'vue'
import VSimpleLogoDark from '@/assets/images/vsimple-auth-logo-dark.svg'
import VaiSparkleIcon from '@/assets/icons/common/vai-sparkle.svg'
import VaiStepCheckIcon from '@/assets/icons/common/vai-step-check.svg'
import VaiStepSpinnerIcon from '@/assets/icons/common/vai-step-spinner.svg'
import { useAuthAiPreview } from '@/compasables/useAuthAiPreview'
import {
  AUTH_AI_PREVIEW_HEADLINE,
  AUTH_AI_PREVIEW_PILL_LABEL,
  AUTH_AI_PREVIEW_PINNED_STEP_INDEX,
  AUTH_AI_PREVIEW_ROW_HEIGHT_PX,
  AUTH_AI_PREVIEW_STEPS,
  AUTH_AI_PREVIEW_STEP_STATES,
} from '@/constants/AUTH_AI_PREVIEW'

const { activeIndex, start } = useAuthAiPreview()

const stepState = (index: number) => {
  if (index < activeIndex.value) {
    return AUTH_AI_PREVIEW_STEP_STATES.done
  }

  if (index === activeIndex.value) {
    return AUTH_AI_PREVIEW_STEP_STATES.active
  }

  return AUTH_AI_PREVIEW_STEP_STATES.pending
}

// The list only scrolls once the active card reaches its pinned row; before
// that the cards stay where the design frame puts them. The offset is capped at
// the last step so the finished list does not drift past it.
const listStyle = computed(() => {
  const lastStepIndex = AUTH_AI_PREVIEW_STEPS.length - 1
  const reachedIndex = Math.min(activeIndex.value, lastStepIndex)
  const scrolledRows = Math.max(0, reachedIndex - AUTH_AI_PREVIEW_PINNED_STEP_INDEX)

  return {
    transform: `translateY(-${scrolledRows * AUTH_AI_PREVIEW_ROW_HEIGHT_PX}px)`,
  }
})

onMounted(start)

const styles = useCssModule()
</script>

<style lang="scss" module>
$headline-color: #250a44;
$pill-bg: #250a44;
$card-bg: #fcfbfa;
$card-border: #d4d2cf;
$card-label: #46454c;
$card-label-pending: #b1b2b5;
$card-active-border: #63159d;
$check-color: #077747;

$card-width: 390px;
$card-height: 58px;
$row-height: 71px;
$pill-height: 48px;
$viewport-gap: 13px;

// The list sits $fade-top below the viewport top so the first card clears the
// top fade at rest; cards only dissolve into it once the list scrolls.
$fade-top: 20px;
$fade-bottom-start: 375px;
$fade-bottom-end: 417px;
$viewport-height: 423px;
$fade-mask: linear-gradient(
  to bottom,
  transparent 0,
  #000 $fade-top,
  #000 $fade-bottom-start,
  transparent $fade-bottom-end
);

.auth-ai-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(630px, 100%);
  margin: 0 auto;
}

.auth-ai-preview__logo {
  width: 191px;
  height: auto;
  margin-bottom: 22px;
}

.auth-ai-preview__headline {
  // Wide enough for the first sentence to hold one line, so the text breaks
  // where the design frame breaks it.
  max-width: 560px;
  margin: 0 0 34px;
  font-family: var(--v-font-primary);
  font-size: 27px;
  font-weight: 700;
  line-height: 1.28;
  text-align: center;
  color: $headline-color;

  @include media-breakpoint-down(lg) {
    font-size: 22px;
  }
}

.auth-ai-preview__stage {
  position: relative;
  width: 100%;
}

// Runs from the middle of the pill down to the top of the list. This stretch
// never has a card over it, so it stays solid.
.auth-ai-preview__connector {
  position: absolute;
  top: $pill-height * .5;
  left: 50%;
  width: 1px;
  height: $pill-height * .5 + $viewport-gap;
  transform: translateX(-50%);
  background-color: $card-border;
}

.auth-ai-preview__pill {
  position: relative;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  width: fit-content;
  height: $pill-height;
  margin: 0 auto;
  padding: 0 24px;
  border-radius: $pill-height * .5;
  background-color: $pill-bg;
  font-family: var(--v-font-primary);
  font-size: 15px;
  font-weight: 500;
  color: #fff;
}

.auth-ai-preview__pill-icon {
  flex-shrink: 0;
  width: 21px;
  height: 21px;
}

.auth-ai-preview__viewport {
  position: relative;
  overflow: hidden;
  height: $viewport-height;
  margin-top: $viewport-gap;
  // A mask rather than a solid overlay, so the fade stays correct whatever the
  // layout paints behind the block.
  -webkit-mask-image: $fade-mask;
  mask-image: $fade-mask;
}

// Spans the list itself, so it ends at the bottom of the last card and travels
// with the rows. Being inside the masked viewport, the cards occlude it before
// the fade is applied — it passes behind them rather than bleeding through.
.auth-ai-preview__list-connector {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 1px;
  transform: translateX(-50%);
  background-color: $card-border;
}

.auth-ai-preview__list {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: $row-height - $card-height;
  margin: 0;
  padding: $fade-top 0 0;
  list-style: none;
  transition: transform .45s cubic-bezier(.4, 0, .2, 1);
}

.auth-ai-preview__step {
  // Positioned so the cards paint over the connector running behind them.
  position: relative;
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: $card-width;
  height: $card-height;
  margin: 0 auto;
  padding: 0 20px;
  border: 1px solid $card-border;
  border-radius: 12px;
  background-color: $card-bg;
  font-family: var(--v-font-primary);
  font-size: 15px;
  color: $card-label;
  transition: border-color .3s ease, box-shadow .3s ease, color .3s ease;
}

.auth-ai-preview__step--active {
  border-color: $card-active-border;
  background-color: #fff;
  box-shadow: 0 5px 15px rgba(99, 21, 157, .25);
}

.auth-ai-preview__step--pending {
  color: $card-label-pending;
}

.auth-ai-preview__step-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.auth-ai-preview__step-check {
  flex-shrink: 0;
  width: 23px;
  height: 23px;
  color: $check-color;
}

.auth-ai-preview__step-spinner {
  flex-shrink: 0;
  width: 25px;
  height: 25px;
  color: $card-label-pending;
}

.auth-ai-preview__step--active .auth-ai-preview__step-spinner {
  color: $card-active-border;
  animation: auth-ai-preview-spin 1s linear infinite;
}

@keyframes auth-ai-preview-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .auth-ai-preview__list,
  .auth-ai-preview__step {
    transition: none;
  }

  .auth-ai-preview__step--active .auth-ai-preview__step-spinner {
    animation: none;
  }
}
</style>
